import React from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import {
  CAMPAIGN_OPTIONS,
  CHILDREN_AGE_OPTIONS,
  DAD_STAGE_OPTIONS,
  MEMBER_TIER_OPTIONS,
  PRESSURE_AREA_OPTIONS,
  SOURCE_OPTIONS,
  WARMTH_OPTIONS,
} from './memberConstants';

const FOCUS_OPTIONS = [
  { id: 'parenting', name: 'Parenting' },
  { id: 'marriage', name: 'Marriage' },
  { id: 'career', name: 'Career' },
  { id: 'general', name: 'Open Discussion' },
];

const MemberEditModal = ({
  show,
  editingMember,
  editMember,
  setEditMember,
  savingEdit,
  uploadingPhoto,
  editMemberPhotoInputRef,
  handleEditMemberPhotoUpload,
  handleSaveMemberEdit,
  onClose,
}) => {
  if (!show || !editingMember) return null;

  const selectedFocusPreferences = editMember.focusPreferences || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="max-h-[90vh] w-full max-w-md overflow-y-auto border-[#2a2a32] bg-[#1e1e24] p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Edit Dad</h3>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm text-gray-400">Profile photo</label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-16 w-16 border-2 border-aqua">
                  <AvatarImage src={editingMember.picture} alt={editingMember.name} />
                  <AvatarFallback className="bg-aqua/20 text-aqua">
                    {(editingMember.name || editingMember.email || '?').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <input
                  type="file"
                  ref={editMemberPhotoInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleEditMemberPhotoUpload}
                />
                <button
                  type="button"
                  onClick={() => editMemberPhotoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-aqua text-charcoal hover:bg-aqua/80 disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">Click to upload new photo</p>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400">Name</label>
            <Input
              placeholder="Full name"
              value={editMember.name}
              onChange={(event) => setEditMember({ ...editMember, name: event.target.value })}
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Email (advanced)</label>
            <Input
              type="email"
              placeholder="email@example.com"
              value={editMember.email}
              onChange={(event) => setEditMember({ ...editMember, email: event.target.value })}
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
            <p className="mt-1 text-xs text-gray-500">Changing this does not change Supabase login email.</p>
          </div>

          <div>
            <label className="text-sm text-gray-400">Phone</label>
            <Input
              type="tel"
              placeholder="Phone number"
              value={editMember.phone}
              onChange={(event) => setEditMember({ ...editMember, phone: event.target.value })}
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-sm text-gray-400">City</label>
              <Input
                placeholder="City"
                value={editMember.city}
                onChange={(event) => setEditMember({ ...editMember, city: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">State</label>
              <Input
                placeholder="State"
                value={editMember.state}
                onChange={(event) => setEditMember({ ...editMember, state: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">Country</label>
              <Input
                placeholder="Country"
                value={editMember.country}
                onChange={(event) => setEditMember({ ...editMember, country: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400">Marriage status</label>
            <Input
              placeholder="Married, divorced, separated, single, widower..."
              value={editMember.marriage_status}
              onChange={(event) =>
                setEditMember({ ...editMember, marriage_status: event.target.value })
              }
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">How long married?</label>
            <Input
              placeholder="12 years or Not applicable"
              value={editMember.married_years}
              onChange={(event) => setEditMember({ ...editMember, married_years: event.target.value })}
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Relationship stage</label>
            <select
              value={editMember.relationship_stage}
              onChange={(event) =>
                setEditMember({ ...editMember, relationship_stage: event.target.value })
              }
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              <option value="">Not set</option>
              {DAD_STAGE_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Tier</label>
            <select
              value={editMember.tier}
              onChange={(event) => setEditMember({ ...editMember, tier: event.target.value })}
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {MEMBER_TIER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Status</label>
            <select
              value={editMember.status}
              onChange={(event) => setEditMember({ ...editMember, status: event.target.value })}
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              <option value="active">Active</option>
              <option value="former">Former</option>
              <option value="trial">Trial</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Billing cycle</label>
            <select
              value={editMember.billing_cycle}
              onChange={(event) =>
                setEditMember({ ...editMember, billing_cycle: event.target.value })
              }
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Campaign</label>
            <select
              value={editMember.campaign_type}
              onChange={(event) =>
                setEditMember({ ...editMember, campaign_type: event.target.value })
              }
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {CAMPAIGN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Source</label>
            <select
              value={editMember.source}
              onChange={(event) => setEditMember({ ...editMember, source: event.target.value })}
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Warmth</label>
            <select
              value={editMember.warmth}
              onChange={(event) => setEditMember({ ...editMember, warmth: event.target.value })}
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {WARMTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Children ages</label>
            <select
              value={editMember.children_age_range}
              onChange={(event) =>
                setEditMember({ ...editMember, children_age_range: event.target.value })
              }
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {CHILDREN_AGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Children summary</label>
            <Textarea
              placeholder="daughter 2, son 4, son 7, daughter 8"
              value={editMember.children_summary}
              onChange={(event) =>
                setEditMember({ ...editMember, children_summary: event.target.value })
              }
              className="mt-1 min-h-[72px] border-[#2a2a32] bg-[#16161a] text-white"
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Biggest pressure area</label>
            <select
              value={editMember.biggest_pressure_area}
              onChange={(event) =>
                setEditMember({ ...editMember, biggest_pressure_area: event.target.value })
              }
              className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
            >
              {PRESSURE_AREA_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Cohorts (comma-separated)</label>
            <Input
              placeholder="e.g. 2026-Q1, founding40"
              value={editMember.cohorts}
              onChange={(event) => setEditMember({ ...editMember, cohorts: event.target.value })}
              className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Bio</label>
            <Textarea
              placeholder="Short bio"
              value={editMember.bio}
              onChange={(event) => setEditMember({ ...editMember, bio: event.target.value })}
              className="mt-1 min-h-[80px] border-[#2a2a32] bg-[#16161a] text-white"
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Notes</label>
            <Textarea
              placeholder="Helpful background, referral context, or webinar notes"
              value={editMember.notes}
              onChange={(event) => setEditMember({ ...editMember, notes: event.target.value })}
              className="mt-1 min-h-[80px] border-[#2a2a32] bg-[#16161a] text-white"
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">What feels heaviest right now?</label>
            <Textarea
              placeholder="Current pressure notes from the Founding 40 join form"
              value={editMember.current_pressure_notes}
              onChange={(event) =>
                setEditMember({ ...editMember, current_pressure_notes: event.target.value })
              }
              className="mt-1 min-h-[96px] border-[#2a2a32] bg-[#16161a] text-white"
              rows={4}
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Focus preferences</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {FOCUS_OPTIONS.map((focus) => {
                const isSelected = selectedFocusPreferences.includes(focus.id);
                return (
                  <button
                    key={focus.id}
                    type="button"
                    onClick={() => {
                      setEditMember((previous) => ({
                        ...previous,
                        focusPreferences: isSelected
                          ? (previous.focusPreferences || []).filter((item) => item !== focus.id)
                          : [...(previous.focusPreferences || []), focus.id],
                      }));
                    }}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      isSelected
                        ? 'border-aqua bg-aqua/20 text-aqua'
                        : 'border-[#2a2a32] bg-[#16161a] text-gray-400 hover:border-[#3a3a42]'
                    }`}
                  >
                    {focus.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#2a2a32] text-white hover:bg-[#16161a]"
            >
              Cancel
            </Button>

            <Button
              disabled={savingEdit}
              onClick={handleSaveMemberEdit}
              className="flex-1 bg-aqua text-charcoal hover:bg-aqua-dark"
            >
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MemberEditModal;
